import HomeOutlined from '@mui/icons-material/HomeOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';

/**
 * Single source of truth for navigation. The desktop navbar, the mobile bottom
 * bar and the "More" sheet all read from this list, so a route can never appear
 * in one and be missing from another.
 *
 * `adminOnly` gates the item against AuthContext.role.
 * `primary` marks the items that earn a slot in the mobile bottom bar.
 */
export const NAV_ITEMS = [
  {
    to: '/',
    label: 'Home',
    icon: HomeOutlined,
    primary: true,
    description: 'Dashboard and quick actions',
  },
  {
    to: '/qdf-main',
    label: 'QDF',
    icon: AssignmentOutlined,
    primary: true,
    description: 'Work orders, PPM schedules and service requests',
  },
  {
    to: '/viewReports',
    label: 'Reports',
    icon: DescriptionOutlined,
    primary: true,
    description: 'View submitted shift and monthly reports',
  },
  {
    to: '/maintenance-analysis',
    label: 'Maintenance Analysis',
    icon: BarChartOutlined,
    adminOnly: true,
    description: 'Monthly CM and PPM graphs from an Excel workbook',
  },
  {
    to: '/attendance',
    label: 'Attendance',
    icon: EventAvailableOutlined,
    description: 'Attendance records, leave requests and shift rosters',
  },
  {
    to: '/add-shift-report',
    label: 'Add Shift Report',
    icon: AddCircleOutline,
    description: 'File today’s shift report',
  },
  {
    to: '/pas-interface',
    label: 'PAS Interface',
    icon: CampaignOutlined,
    description: 'Public Address System devices and controllers',
  },
  {
    to: '/rca-main',
    label: 'Root Cause Analysis',
    icon: WarningAmberOutlined,
    adminOnly: true,
    description: 'Document and track incident analyses',
  },
  {
    to: '/add-monthly-report',
    label: 'Add Monthly Report',
    icon: CalendarMonthOutlined,
    adminOnly: true,
    description: 'Compile the monthly report',
  },
  {
    to: '/system-report',
    label: 'System Report',
    icon: AssessmentOutlined,
    adminOnly: true,
    description: 'Prepare a PA system report from a CM / PPM workbook',
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: SettingsOutlined,
    description: 'Account and application settings',
  },
];

/** Items the given role is allowed to see. */
export const visibleNavItems = (role) =>
  NAV_ITEMS.filter((item) => !item.adminOnly || role === 'admin');

/** The 3 bottom-bar tabs; "More" holds everything else. */
export const primaryNavItems = (role) => visibleNavItems(role).filter((item) => item.primary);

export const secondaryNavItems = (role) => visibleNavItems(role).filter((item) => !item.primary);

/**
 * Matches a pathname to a nav item. Sub-routes (e.g. /qdf-ppm-form, /view-shift-report/:id)
 * highlight the module they belong to rather than nothing at all.
 */
const SUBROUTE_OWNERS = [
  { prefix: '/qdf', owner: '/qdf-main' },
  { prefix: '/view-shift-report', owner: '/viewReports' },
  { prefix: '/view-monthly-report', owner: '/viewReports' },
  { prefix: '/add-monthly-report', owner: '/add-monthly-report' },
];

export const activeNavPath = (pathname) => {
  if (pathname === '/') return '/';

  const exact = NAV_ITEMS.find((item) => item.to !== '/' && item.to === pathname);
  if (exact) return exact.to;

  const owned = SUBROUTE_OWNERS.find((rule) => pathname.startsWith(rule.prefix));
  if (owned) return owned.owner;

  const partial = NAV_ITEMS.find((item) => item.to !== '/' && pathname.startsWith(item.to));
  return partial ? partial.to : null;
};
